import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useToast } from '../../context/ToastContext';
import MessageBubble from './MessageBubble';
import WallProfileModal from '../Wall/WallProfileModal';
import { SendIcon } from '../../utils/icons';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)',
    background: 'var(--bg-surface)',
    flexShrink: 0,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    minWidth: 44,
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarWrap: {
    position: 'relative',
    flexShrink: 0,
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
    background: 'var(--bg-elevated)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    color: 'var(--text-muted)',
  },
  headerInfo: {
    flex: 1,
  },
  headerName: {
    fontSize: '15px',
    fontWeight: '600',
  },
  headerStatus: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  typing: {
    padding: '4px 16px',
    fontSize: '12px',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
    flexShrink: 0,
  },
  inputBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    borderTop: '1px solid var(--border-color)',
    background: 'var(--bg-surface)',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    padding: '10px 16px',
    borderRadius: '20px',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'none',
    overflow: 'hidden',
    minHeight: '40px',
    maxHeight: '120px',
    lineHeight: 1.4,
  },
  sendBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
};

export default function ChatRoom({ matchId }) {
  const { user } = useAuth();
  const {
    sendMessage: socketSendMessage,
    sendTypingIndicator,
    sendReadReceipt,
    incomingMessage,
    readReceipts,
    typingIndicators,
    onlineUsers,
  } = useSocket();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileUserId, setProfileUserId] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const pendingMsgsRef = useRef([]);  // optimistic messages awaiting server confirmation
  const textareaRef = useRef(null);
  const pollFailCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Load messages and match info
  useEffect(() => {
    const load = async () => {
      try {
        const [msgRes, matchRes] = await Promise.all([
          api.get(`/messages/${matchId}`),
          api.get('/matches'),
        ]);

        setMessages(msgRes.data);

        const match = matchRes.data.find((m) => m.id === parseInt(matchId));
        if (match) {
          setOtherUser(match.other_user);
        }
      } catch {
        // ignore
        showToast('Couldn\'t load messages', 'error');
      } finally {
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    };

    load();
  }, [matchId]);

  // Handle incoming messages (from other user via socket)
  useEffect(() => {
    if (
      incomingMessage &&
      incomingMessage.match_id === parseInt(matchId) &&
      incomingMessage.sender_id !== user?.id
    ) {
      setMessages((prev) => {
        // Avoid duplicates by real DB id
        if (prev.some((m) => m.id === incomingMessage.id)) return prev;
        return [...prev, incomingMessage];
      });

      // Send read receipt
      sendReadReceipt(parseInt(matchId), incomingMessage.id);
    }
  }, [incomingMessage, matchId, user?.id, sendReadReceipt]);

  // Handle read receipts — mark sent messages as read in the UI
  useEffect(() => {
    if (
      readReceipts &&
      readReceipts.matchId === parseInt(matchId)
    ) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === readReceipts.messageId && !m.read_at
            ? { ...m, read_at: new Date().toISOString() }
            : m
        )
      );
    }
  }, [readReceipts, matchId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: isInitialLoadRef.current ? 'instant' : 'smooth',
    });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }, [newMsg]);

  // REST polling for new messages — starts after 10s on the chat view, then every 5s
  useEffect(() => {
    let pollInterval = null;

    const startDelay = setTimeout(() => {
      const pollMessages = async () => {
        try {
          const res = await api.get(`/messages/${matchId}`);
          setMessages((prev) => {
            const serverMsgs = res.data;

            // Remove confirmed optimistic messages from pending list.
            // A pending msg is "confirmed" if the server has a message with
            // the same sender_id and content.
            const confirmedContents = new Set();
            for (const p of pendingMsgsRef.current) {
              if (serverMsgs.some((m) => m.sender_id === p.sender_id && m.content === p.content)) {
                confirmedContents.add(p.tempId);
              }
            }
            pendingMsgsRef.current = pendingMsgsRef.current.filter(
              (p) => !confirmedContents.has(p.tempId) && Date.now() - p.sentAt < 30000
            );

            // Keep only optimistic messages still awaiting confirmation
            const pendingIds = new Set(pendingMsgsRef.current.map((p) => p.tempId));
            const optimisticToKeep = prev.filter((m) => m._optimistic && pendingIds.has(m.id));

            // Server messages are the source of truth — append any still-pending optimistic ones
            const merged = [...serverMsgs, ...optimisticToKeep];

            // Avoid unnecessary re-render if nothing changed
            if (merged.length === prev.length && merged.every((m, i) => m.id === prev[i]?.id)) {
              return prev;
            }

            return merged;
          });
        } catch {
          // ignore polling errors
          pollFailCountRef.current += 1;
          if (pollFailCountRef.current >= 3) {
            showToast('Connection issues — retrying', 'error');
            pollFailCountRef.current = 0;
          }
        }
      };

      // Run immediately on first tick, then every 5s
      pollMessages();
      pollInterval = setInterval(pollMessages, 5000);
    }, 10000); // 10s delay before starting

    return () => {
      clearTimeout(startDelay);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [matchId]);

  const handleSend = useCallback(() => {
    const content = newMsg.trim();
    if (!content) return;

    // Optimistic update — use a prefixed ID so polling can identify these
    const tempId = `pending_${Date.now()}`;
    const optimisticMsg = {
      id: tempId,
      _optimistic: true,
      match_id: parseInt(matchId),
      sender_id: user.id,
      content,
      read_at: null,
      created_at: new Date().toISOString(),
    };
    pendingMsgsRef.current.push({ tempId, content, sender_id: user.id, sentAt: Date.now() });
    setMessages((prev) => [...prev, optimisticMsg]);
    setNewMsg('');

    // Send via socket
    socketSendMessage(parseInt(matchId), content);

    // Stop typing indicator
    sendTypingIndicator(parseInt(matchId), false);
  }, [newMsg, matchId, user?.id, socketSendMessage, sendTypingIndicator]);

  const handleInputChange = (e) => {
    setNewMsg(e.target.value);

    // Typing indicator
    sendTypingIndicator(parseInt(matchId), true);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(parseInt(matchId), false);
    }, 2000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const typing = typingIndicators[parseInt(matchId)];
  const isOnline = otherUser && onlineUsers.has(otherUser.id);

  if (loading) {
    return (
      <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end' }}>
            <div className="skeleton" style={{ width: i % 2 === 0 ? 200 : 160, height: 36, borderRadius: 18 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/matches')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, cursor: 'pointer' }}
          onClick={() => otherUser?.id && setProfileUserId(otherUser.id)}
        >
          <div style={styles.avatarWrap}>
            <div style={styles.avatar}>
              {otherUser?.photo ? (
                <img
                  src={`/uploads/${otherUser.photo}`}
                  alt=""
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                otherUser?.display_name?.[0]?.toUpperCase() || '?'
              )}
            </div>
            {isOnline && <span className="online-dot" />}
          </div>
          <div style={styles.headerInfo}>
            <div style={styles.headerName}>{otherUser?.display_name || 'Chat'}</div>
            <div style={styles.headerStatus}>
              {isOnline ? 'Online' : 'Offline'}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.messages}>
        {messages.length === 0 && (
          <div className="empty-state" style={{ padding: '40px 0' }}>
            <p style={{ color: 'var(--text-muted)' }}>
              Say hello to {otherUser?.display_name || 'your match'}!
            </p>
          </div>
        )}

        {messages.map((msg, idx) => {
          const isSent = msg.sender_id === user?.id;
          const nextMsg = messages[idx + 1];
          const showTime =
            !nextMsg ||
            nextMsg.sender_id !== msg.sender_id ||
            new Date(nextMsg.created_at) - new Date(msg.created_at) > 60000;

          const prevMsg = messages[idx - 1];
          const showDateHeader = !prevMsg || new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();

          return (
            <div key={msg.id}>
              {showDateHeader && (
                <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', padding: '12px 0' }}>
                  {formatDateHeader(msg.created_at)}
                </div>
              )}
              <MessageBubble
                message={msg}
                isSent={isSent}
                showTime={showTime}
              />
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {typing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 12px', fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
          <span>{typing.display_name} is typing</span>
          <span className="typing-dots">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </span>
        </div>
      )}

      <div style={{ flexShrink: 0 }}>
        <div style={styles.inputBar}>
          <textarea
            ref={textareaRef}
            style={styles.input}
            value={newMsg}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            maxLength={2000}
            rows={1}
          />
          <button
            style={{ ...styles.sendBtn, opacity: newMsg.trim() ? 1 : 0.5 }}
            onClick={handleSend}
            disabled={!newMsg.trim()}
          >
            <SendIcon size={18} />
          </button>
        </div>
        <span style={{ fontSize: 11, color: newMsg.length > 1800 ? 'var(--warning, #f59e0b)' : 'var(--text-muted)', textAlign: 'right', padding: '2px 8px', display: 'block' }}>
          {newMsg.length}/2000
        </span>
      </div>

      {/* Profile modal */}
      {profileUserId && (
        <WallProfileModal
          userId={profileUserId}
          onClose={() => setProfileUserId(null)}
        />
      )}
    </div>
  );
}
